import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { types as coreTypes } from '@sitebuilder/core';
import { localization as commonLocalization } from '@sitebuilder/common';
import { ContentListApiClient } from '../api/contentListApiClient';
import { ThunkResult } from './index';

export interface StateShape {
    contentList: coreTypes.ContentList | null;
    isContentListLoaded: boolean;
    contentListLoading: boolean;
    contentListLoadError: any | null;
    contentListSaveLoading: boolean;
    contentListSaveSucess: string;
    contentListSaveError: any | null;
    contentLists: [coreTypes.ContentList] | null;
    contentListsLoading: boolean;
    contentListsLoadError: any | null;
    selectedContentList: string[];
    versions: any[] | null;
    versionsLoading: boolean;
    versionsLoadError: any | null;
    revertedVersion: boolean;
    revertedVersionError: any | null;
    showDeletedItems: boolean;
    newFolderSaveLoading: boolean;
    newFolderSaveError: any | null;
    newFolderSaveSucess: string;
}

export const contentListDefaultState: StateShape = {
    contentList: null,
    isContentListLoaded: false,
    contentListLoading: false,
    contentListLoadError: null,
    contentListSaveLoading: false,
    contentListSaveSucess: '',
    contentListSaveError: null,
    contentLists: null,
    contentListsLoading: false,
    contentListsLoadError: null,
    selectedContentList: [],
    versions: null,
    versionsLoading: false,
    versionsLoadError: null,
    revertedVersion: false,
    revertedVersionError: null,
    showDeletedItems: false,
    newFolderSaveLoading: false,
    newFolderSaveError: null,
    newFolderSaveSucess: '',
};

const apiClient = new ContentListApiClient();

export const actionCreators = {
    loadContentList: (
        contentListId: string
    ): ThunkResult<Promise<coreTypes.ContentList | undefined>> => {
        return async (dispatch, getState) => {
            const state = getState();
            const accessToken = state.auth.user?.access_token;
            dispatch(contentListReducerActions.CONTENTLIST_LOAD_START());

            try {
                const contentList = await apiClient.getContentList(contentListId, {
                    accessToken,
                });
                dispatch(contentListReducerActions.CONTENTLIST_LOAD_END({ contentList }));

                if (contentList) {
                    dispatch(contentListReducerActions.SET_IS_CONTENTLIST_LOADED(true));
                }

                return contentList;
            } catch (err: any) {
                console.error(err);
                dispatch(contentListReducerActions.SET_IS_CONTENTLIST_LOADED(true));
                dispatch(
                    contentListReducerActions.CONTENTLIST_LOAD_END({
                        error: { message: err.message },
                    })
                );
            }
        };
    },
    saveNewContentList: (
        contentListId: string,
        contentList?: any
    ): ThunkResult<Promise<boolean | void>> => {
        return async (dispatch, getState) => {
            const state = getState();
            const locale = state?.templateEditor?.documentMetadata?.locale || 'en-US';
            const appI18n = commonLocalization.getLocalization(locale);

            const accessToken = state.auth.user?.access_token;
            dispatch(contentListReducerActions.CONTENTLIST_SAVE_START());

            try {
                await apiClient.saveContentList(contentListId, contentList, {
                    accessToken,
                });
                dispatch(
                    contentListReducerActions.CONTENTLIST_SAVE_END({
                        success: appI18n.t('contentList_form_save_success'),
                    })
                );
                return true;
            } catch (err: any) {
                console.error(err);
                dispatch(
                    contentListReducerActions.CONTENTLIST_SAVE_END({
                        error: { message: err.message },
                    })
                );
                return false;
            }
        };
    },
    saveNewFolder: (folderName: string): ThunkResult<Promise<boolean | void>> => {
        return async (dispatch, getState) => {
            const state = getState();
            const locale = state?.templateEditor?.documentMetadata?.locale || 'en-US';
            const appI18n = commonLocalization.getLocalization(locale);

            const accessToken = state.auth.user?.access_token;
            dispatch(contentListReducerActions.NEWFOLDER_SAVE_START());

            try {
                await apiClient.saveNewFolder(folderName, {
                    accessToken,
                });
                dispatch(
                    contentListReducerActions.NEWFOLDER_SAVE_END({
                        success: appI18n.t('folder_form_save_success'),
                    })
                );
                return true;
            } catch (err: any) {
                console.error(err);
                dispatch(
                    contentListReducerActions.NEWFOLDER_SAVE_END({
                        error: { message: err.message },
                    })
                );
                return false;
            }
        };
    },
    listContentLists: (
        folderName: string,
        filters: { idPattern?: string[]; authorsIn?: Record<string, unknown> | [] } = {
            idPattern: [],
            authorsIn: [],
        },
        sort?: { field?: string; dir?: string },
        take?: number,
        skip?: number
    ): ThunkResult<Promise<void>> => {
        return async (dispatch, getState) => {
            const state = getState();
            const accessToken = state.auth.user?.access_token;
            dispatch(contentListReducerActions.CONTENTLISTS_LOAD_START());

            try {
                const contentLists = await apiClient.listContentLists(
                    folderName,
                    state.contentList.showDeletedItems,
                    {
                        accessToken,
                    },
                    filters,
                    sort,
                    take,
                    skip
                );
                dispatch(contentListReducerActions.CONTENTLISTS_LOAD_END({ contentLists }));
            } catch (err: any) {
                console.error(err);
                dispatch(
                    contentListReducerActions.CONTENTLISTS_LOAD_END({
                        error: { message: err.message },
                    })
                );
            }
        };
    },
    setSelectedContentList: (selectedContentList: string[]): ThunkResult<Promise<void>> => {
        return async (dispatch) => {
            dispatch(contentListReducerActions.SET_SELECTED_CONTENTLIST(selectedContentList));
        };
    },
    unselectContentList: (selectedContentList: string[]): ThunkResult<Promise<void>> => {
        return async (dispatch) => {
            dispatch(contentListReducerActions.REMOVE_SELECTED_CONTENTLIST(selectedContentList));
        };
    },
    unselectAllContentList: (): ThunkResult<Promise<void>> => {
        return async (dispatch) => {
            dispatch(contentListReducerActions.REMOVE_ALL_SELECTED_CONTENTLIST());
        };
    },
    loadContentListVersions: (contentListName: string): ThunkResult<Promise<void>> => {
        return async (dispatch, getState) => {
            const state = getState();
            const accessToken = state.auth.user?.access_token;
            dispatch(contentListReducerActions.VERSIONS_LOAD_START());

            try {
                const items = await apiClient.getContentListVersions(contentListName, {
                    accessToken,
                });
                dispatch(
                    contentListReducerActions.VERSIONS_LOAD_END({
                        versions: items,
                    })
                );
            } catch (err: any) {
                dispatch(
                    contentListReducerActions.VERSIONS_LOAD_END({
                        error: { message: err.message },
                    })
                );
            }
        };
    },

    revertVersions: (contentListName: string | null, versionNumber: any) => {
        return async (dispatch: any, getState: any) => {
            const state = getState();
            const accessToken = state.auth.user?.access_token;
            try {
                const results = await apiClient.revertVersion(contentListName, versionNumber, {
                    accessToken,
                });
                const revertedItem = await results.json();

                if (!isNaN(revertedItem.VersionNumber)) {
                    dispatch(contentListReducerActions.REVERTED_VERSION({}));
                }
            } catch (err: any) {
                dispatch(
                    contentListReducerActions.REVERTED_VERSION({
                        error: { message: err.message },
                    })
                );
            }
        };
    },

    setShowDeletedItems: (): ThunkResult<Promise<void>> => {
        return async (dispatch, getState) => {
            const state = getState();
            dispatch(
                contentListReducerActions.SET_SHOW_DELETED_ITEMS(
                    !state.contentList.showDeletedItems
                )
            );
        };
    },
};

const contentListSlice = createSlice({
    name: 'contentLists',
    initialState: contentListDefaultState,
    reducers: {
        CONTENTLIST_LOAD_START(draft) {
            draft.contentListLoading = true;
            draft.contentList = null;
            draft.contentListLoadError = null;
            draft.contentListSaveSucess = '';
            draft.newFolderSaveSucess = '';
        },
        CONTENTLIST_LOAD_END(
            draft,
            action: PayloadAction<{ contentList?: coreTypes.ContentList; error?: any }>
        ) {
            const { contentList, error } = action.payload;
            draft.contentListLoading = false;
            draft.contentList = contentList || null;
            draft.contentListLoadError = error;
        },
        CONTENTLIST_SAVE_START(draft) {
            draft.contentListSaveLoading = true;
            draft.contentListSaveError = null;
        },
        CONTENTLIST_SAVE_END(draft, action: PayloadAction<{ success?: string; error?: any }>) {
            const { success, error } = action.payload;
            draft.contentListSaveLoading = false;
            draft.contentListSaveSucess = success || '';
            draft.contentListSaveError = error;
        },
        CONTENTLISTS_LOAD_START(draft) {
            draft.contentListsLoading = true;
            draft.contentLists = null;
            draft.contentListsLoadError = null;
        },
        CONTENTLISTS_LOAD_END(
            draft,
            action: PayloadAction<{ contentLists?: [coreTypes.ContentList]; error?: any }>
        ) {
            const { contentLists, error } = action.payload;
            draft.contentListsLoading = false;
            draft.contentLists = contentLists || null;
            draft.contentListsLoadError = error;
            draft.revertedVersion = false;
        },
        NEWFOLDER_SAVE_START(draft) {
            draft.newFolderSaveLoading = true;
            draft.newFolderSaveError = null;
        },
        NEWFOLDER_SAVE_END(draft, action: PayloadAction<{ success?: string; error?: any }>) {
            const { success, error } = action.payload;
            draft.newFolderSaveLoading = false;
            draft.newFolderSaveSucess = success || '';
            draft.newFolderSaveError = error;
        },
        SET_IS_CONTENTLIST_LOADED(draft, action: PayloadAction<boolean>) {
            draft.isContentListLoaded = action.payload;
        },
        SET_SELECTED_CONTENTLIST(draft, action: PayloadAction<string[]>) {
            draft.selectedContentList = [...draft.selectedContentList, ...action.payload];
        },
        REMOVE_SELECTED_CONTENTLIST(draft, action: PayloadAction<string[]>) {
            draft.selectedContentList = draft.selectedContentList.filter(
                (contentList) => !action.payload.includes(contentList)
            );
        },
        REMOVE_ALL_SELECTED_CONTENTLIST(draft) {
            draft.selectedContentList = [];
        },
        VERSIONS_LOAD_START(draft) {
            draft.versionsLoading = true;
            draft.versions = null;
            draft.versionsLoadError = null;
        },
        VERSIONS_LOAD_END(draft, action: PayloadAction<{ versions?: any[]; error?: any }>) {
            const { versions, error } = action.payload;
            draft.versionsLoading = false;
            draft.versions = versions || null;
            draft.versionsLoadError = error;
            draft.revertedVersion = false;
        },
        REVERTED_VERSION(draft, action: PayloadAction<{ error?: any }>) {
            draft.revertedVersion = true;
            draft.revertedVersionError = action.payload.error;
        },
        SET_SHOW_DELETED_ITEMS(draft, action: PayloadAction<boolean>) {
            draft.showDeletedItems = action.payload;
        },
    },
});

export const contentListReducerActions: any = contentListSlice.actions;
export const contentListReducer = contentListSlice.reducer;
